import { moneyExact } from "./lib/money";
/** The lead as v2 knows it — a typed, validated projection of the
 * `properties` table. `narrowLead` is the ONLY door server rows enter
 * through: anything malformed is dropped and counted, never rendered. */

export interface Lead {
  id: string;
  createdAt: number; // epoch ms
  address: string;          // IDENTITY key (nat_key) — never change for display
  displayAddress?: string;  // title only — full "street, city, ST zip" when known
  source: string;
  price: number | null;
  arv: number | null;
  arvPct: number | null; // asking/ARV %
  county: string | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  // v1-parity metrics (2026-07-16): same raw fields v1's mapScraped reads
  zest: number | null; // raw.zestimate
  rentZest: number | null; // raw.rent_zestimate
  cdom: number | null; // raw.cdom ?? days_on_zillow ?? dom (days)
  seller: string | null; // agent/seller · company (Zillow rows: agent_name)
  // v1-parity badges (mapScraped + _leadBadges)
  marketStatus: "on" | "off" | null; // raw.market_status || raw.home_status
  priceDrop: { pct: number; from: number | null; to: number | null } | null; // raw.price_drop — GENUINE in-CRM drops only (server rule 2026-07-16)
  reductions: number; // raw.price_drops length — includes pre-CRM history entries
  priceDrops: PriceDropEntry[]; // raw.price_drops — the on-lead reduction log
  priceHistory: PriceHistoryEntry[]; // raw.price_history — Zillow listing events
  photos: string[]; // raw.images || raw.image_urls (comma lists, like mapScraped)
  desc: string | null; // raw.description — v1 LISTING OVERVIEW card
  taxHistory: TaxHistoryEntry[]; // raw.tax_history — v1 PUBLIC TAX HISTORY table
  yearBuilt: number | null; // raw.year_built ?? raw.year — v1 p.year (comps prefill)
  /** raw-twin display fallbacks for the property-field grid, keyed by the
   *  edit-field key (owner/parcel/yearBuilt/…). Optional so manual-lead
   *  mappers and fixtures don't have to carry it. */
  facts?: Record<string, string>;
  /** raw.possible_dup_of — the scraper's duplicate radar found another live
   *  lead with the same canonical address (St vs Street drift). Renders as a
   *  chip on the card + a banner naming the twin (Tom 2026-08-17). */
  possibleDupOf?: string | null;
  permits: unknown; // raw.permits — stamped by the county scraper / AI search (narrowed by the Permits feature)
  permitsChecked: boolean; // raw.permits_checked ?? raw.permitsChecked
  permitUrl: string | null; // raw.permit_url ?? raw.permitUrl — county portal deep link
  distressed?: boolean; // raw.distressed — v1 p.distressed (distressed-condition flag); absent = not flagged
  jobTag?: string; // raw.jobTag / raw.job_tag — which scraper job produced this lead (v1 p.jobTag)
  ilUrl?: string | null; // raw.property_page_url — the InvestorLift marketplace page (IL leads only)
  /** raw.property_url when it's a zillow.com page — the auto-enrich stamps
   *  this on SMS/InvestorLift leads too (2026-09-30); the Links card shows
   *  it whenever the user hasn't typed their own Zillow link */
  zillowUrl?: string | null;
  countyLink?: string | null; // raw.county_appraiser ?? county_link — county appraiser record deep link (v1 p.countyLink)
  countySite?: string | null; // raw.county_site — county appraiser portal home (v1 p.countySite)
  mediaLinks?: { label: string; url: string }[]; // scraped photo/video links (v1 p.mediaLinks)
  photoLinks?: { label: string; url: string }[]; // photos/docs the seller or agent sent (v1 p.photoLinks)
}

export interface TaxHistoryEntry {
  year: number | null; // r.year, else derived from epoch-ms r.time (v1 rule)
  tax: number | null;
  assessment: number | null;
}

export interface PriceDropEntry {
  date: string | null; // real cut date when history knew it
  from: number | null;
  to: number | null;
  pct: number | null;
  preCrm: boolean; // true = happened before the lead entered the CRM (server rule 2026-07-16)
  source: string | null;
}

export interface PriceHistoryEntry {
  date: string | null;
  event: string | null; // "Listed for sale", "Price change", …
  price: number | null;
  pct: number | null; // priceChangeRate × 100 (v1 shows ±x.x%)
  ppsf: number | null; // pricePerSquareFoot
  source: string | null;
}

interface RawRow {
  id?: unknown;
  created_at?: unknown;
  address?: unknown;
  source?: unknown;
  price?: unknown;
  arv?: unknown;
  county?: unknown;
  beds?: unknown;
  baths?: unknown;
  sqft?: unknown;
  raw?: unknown;
}

interface RawBlob {
  arv_percentage?: unknown;
  zestimate?: unknown;
  rent_zestimate?: unknown;
  cdom?: unknown;
  days_on_zillow?: unknown;
  dom?: unknown;
  agent_name?: unknown;
  seller?: unknown;
  company_name?: unknown;
  /** InvestorLift rows (2026-07-22, live-row audit): the "name" v1 shows is
   * the IL dispositions manager; the wholesaler company is account_title. */
  dispositions_manager?: unknown;
  account_title?: unknown;
  /** IL marketplace page (Tom 2026-08-10: "it should show the investorlift
   * link") — e.g. investorlift.com/marketplace/p/333322. Zillow rows carry
   * their listing in property_url instead. */
  property_page_url?: unknown;
  property_url?: unknown;          // Zillow homedetails URL
  /** Zillow rows set the `address` COLUMN to the STREET only; the full
   * "street, city, ST zip" lives here (Tom 2026-08-12: "address isnt full"). */
  full_address?: unknown;
  google_drive_links?: unknown;
  market_status?: unknown;
  home_status?: unknown;
  price_drop?: unknown;
  price_drops?: unknown;
  price_history?: unknown;
  images?: unknown;
  image_urls?: unknown;
  description?: unknown;
  tax_history?: unknown;
  taxHistory?: unknown;
  year_built?: unknown;
  year?: unknown;
  permits?: unknown;
  /** the auto-enrich's county-portal permits — a JSON STRING (the scraper
   * json.dumps it) or an array; merged into `permits` for display */
  county_permits?: unknown;
  county_fill_at?: unknown;
  permits_checked?: unknown;
  permitsChecked?: unknown;
  permit_url?: unknown;
  permitUrl?: unknown;
  county_appraiser?: unknown;
  county_link?: unknown;
  countyLink?: unknown;
  county_site?: unknown;
  countySite?: unknown;
  media_links?: unknown;
  mediaLinks?: unknown;
  photo_links?: unknown;
  photoLinks?: unknown;
  contact_docs?: unknown;
}

/** v1 scraped photo/video/doc links — an array of {label,url} or bare URL
 * strings; anything non-http is dropped. Shared by p.mediaLinks / p.photoLinks. */
function linkListOf(v: unknown): { label: string; url: string }[] {
  if (!Array.isArray(v)) return [];
  const out: { label: string; url: string }[] = [];
  for (const e of v.slice(0, 24)) {
    if (typeof e === "string") {
      if (/^https?:\/\//i.test(e)) out.push({ label: "Link", url: e });
    } else if (e && typeof e === "object") {
      const o = e as Record<string, unknown>;
      const url = typeof o["url"] === "string" ? o["url"] : typeof o["href"] === "string" ? o["href"] : "";
      if (/^https?:\/\//i.test(url)) out.push({ label: typeof o["label"] === "string" && o["label"] ? (o["label"] as string) : "Link", url });
    }
  }
  return out;
}

/** v1 mapScraped: `raw.images` (Zillow) else `raw.image_urls` (InvestorLift),
 * comma-separated URL lists. Capped defensively — some IL rows carry 30+. */
function priceDropsOf(raw: RawBlob): PriceDropEntry[] {
  if (!Array.isArray(raw.price_drops)) return [];
  const out: PriceDropEntry[] = [];
  for (const e of raw.price_drops.slice(-30)) {
    if (e === null || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    out.push({
      date: str(o["date"]) ?? (str(o["at"]) ? String(o["at"]).slice(0, 10) : null),
      from: num(o["from"]),
      to: num(o["to"]) ?? num(o["price"]),
      pct: num(o["pct"]),
      preCrm: o["pre_crm"] === true,
      source: str(o["source"]),
    });
  }
  return out.reverse(); // newest first for display
}

function priceHistoryOf(raw: RawBlob): PriceHistoryEntry[] {
  if (!Array.isArray(raw.price_history)) return [];
  const out: PriceHistoryEntry[] = [];
  for (const e of raw.price_history.slice(0, 40)) {
    if (e === null || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const date = str(o["date"]) ?? str(o["time"]) ?? null;
    const event = str(o["event"]);
    const price = num(o["price"]);
    if (!date && !event && price === null) continue;
    const rate = num(o["priceChangeRate"]);
    out.push({
      date, event, price,
      pct: rate !== null && rate !== 0 ? Math.round(rate * 1000) / 10 : null,
      ppsf: num(o["pricePerSquareFoot"]),
      source: str(o["source"]),
    });
  }
  // newest first (Zillow history arrives newest-first already, but don't assume)
  out.sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? "")));
  return out;
}

/** v1 _taxHistoryHTML: rows {year|time(epoch ms), tax, assessment}. */
function taxHistoryOf(raw: RawBlob): TaxHistoryEntry[] {
  const src = Array.isArray(raw.tax_history) ? raw.tax_history : Array.isArray(raw.taxHistory) ? raw.taxHistory : [];
  const out: TaxHistoryEntry[] = [];
  for (const e of src.slice(0, 30)) {
    if (e === null || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    let year = num(o["year"]);
    if (year === null) {
      const t = num(o["time"]);
      if (t !== null) {
        const d = new Date(t);
        year = Number.isNaN(d.getTime()) ? null : d.getFullYear();
      }
    }
    const tax = num(o["tax"]);
    const assessment = num(o["assessment"]);
    if (year === null && tax === null && assessment === null) continue;
    out.push({ year, tax, assessment });
  }
  return out;
}

function photosOf(raw: RawBlob): string[] {
  const src = raw.images ?? raw.image_urls;
  // Zillow: comma-separated string. InvestorLift: a REAL array of URLs
  // (2026-07-22 — the string-only parse left IL rows with no photos at all).
  const list: string[] = [];
  if (typeof src === "string") list.push(...src.split(","));
  else if (Array.isArray(src)) for (const u of src) if (typeof u === "string") list.push(u);
  // 24 -> 60 -> 90 (Tom 2026-08-13: raise the cap again). It was US capping,
  // in two places: this slice AND the Zillow scraper's cap (both moved to 90).
  // InvestorLift rows already STORE the full set, so raising this shows the
  // rest immediately; Zillow rows grow on their next scrape.
  return list.map((s) => s.trim()).filter((s) => /^https?:\/\//.test(s)).slice(0, 90);
}

/** v1 mapScraped's on/off-market mapping, verbatim semantics. */
function marketStatusOf(raw: RawBlob): "on" | "off" | null {
  const ms = str(raw.market_status) ?? str(raw.home_status);
  if (!ms) return null;
  const m = ms.toLowerCase();
  if (m === "on_market" || m.includes("for_sale") || m.includes("for sale") || m === "available" || m.includes("coming")) return "on";
  if (m === "off_market" || m.includes("off") || m.includes("sold") || m.includes("pending")) return "off";
  return null;
}

/** v1 p.jobTag — the scraper job that produced this lead (raw.jobTag/job_tag).
 * Empty string when untagged (keeps the field a plain string under
 * exactOptionalPropertyTypes; the filter/options treat "" as "no tag"). */
function jobTagOf(raw: RawBlob): string {
  const o = raw as unknown as Record<string, unknown>;
  const t = typeof o["jobTag"] === "string" ? o["jobTag"] : typeof o["job_tag"] === "string" ? o["job_tag"] : "";
  return (t as string).trim();
}

/** Edit-field key -> the raw keys enrich/scrape store the same fact under.
 *  First non-empty wins; values become plain strings (the grid's inputs are
 *  text). Selects are deliberately excluded — free-text raw values don't
 *  reliably match their option lists. */
const FACT_TWINS: [string, string[]][] = [
  ["owner", ["county_owner"]],
  ["parcel", ["parcel_number"]],
  ["yearBuilt", ["year_built", "year"]],
  ["published", ["listed_date", "published_date"]],
  ["daysOnZillow", ["days_on_zillow", "zillow_days_on_market"]],
  ["lotSize", ["lot_size"]],
  ["lotAcres", ["lot_size_acres"]],
  ["houseSqft", ["sq_footage"]],
  ["countySqft", ["county_living_sqft"]],
  ["totalSqft", ["county_gross_area"]],
  ["subdivision", ["county_subdivision"]],
  ["countyTax", ["county_annual_tax", "zillow_last_tax_amount"]],
  // listing-agent fallbacks (Tom 2026-08-20, 1502 Egan Dr: "why ... the
  // realtor wasnt added to the lead" — Zillow scrapes carry the agent in raw;
  // these feed the Seller card + field grid when nothing was typed)
  ["who", ["agent_name", "dispositions_manager"]],
  ["phone", ["agent_phone", "seller_phone", "contact_phone"]],
];

function factsOf(raw: RawBlob): Record<string, string> {
  const r = raw as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const [key, twins] of FACT_TWINS) {
    for (const t of twins) {
      const v = r[t];
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s && s !== "0") { out[key] = s; break; }
    }
  }
  return out;
}

function priceDropOf(raw: RawBlob): Lead["priceDrop"] {
  const pd = raw.price_drop;
  if (pd === null || typeof pd !== "object" || Array.isArray(pd)) return null;
  const o = pd as Record<string, unknown>;
  const pct = num(o["pct"]);
  if (pct === null || pct <= 0) return null; // v1 gates the badge on .pct
  return { pct, from: num(o["from"]), to: num(o["to"]) };
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const f = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(f) ? f : null;
};

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** raw.county_permits — what the AUTO-ENRICH writes (county portal rows:
 * number/type/issue_date/description/amount/status). The scraper stores it
 * as a JSON string; older rows may be arrays. Tom 2026-09-14: "the county
 * enrich and permits dont work automatically" — they ran fine (1025 8th St
 * W: 3 Manatee permits an hour after the lead landed); the drawer only ever
 * read raw.permits, so the county rows were invisible. */
export function countyPermitsOf(raw: { county_permits?: unknown }): Record<string, unknown>[] {
  let v = raw.county_permits;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t.startsWith("[")) return [];
    try { v = JSON.parse(t); } catch { return []; }
  }
  return Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => x !== null && typeof x === "object" && !Array.isArray(x)) : [];
}

/** Everything the lead knows about permits: the county rows first (tagged
 * source "county" for the badge), then AI / realtor rows from raw.permits,
 * deduped by permit number. */
export function mergedPermitsOf(raw: { permits?: unknown; county_permits?: unknown }): unknown[] {
  const county = countyPermitsOf(raw).map((p) => ({ source: "county", ...p }));
  const rest = Array.isArray(raw.permits) ? raw.permits.filter((x) => x !== null && typeof x === "object") : [];
  const seen = new Set<string>();
  const out: unknown[] = [];
  for (const p of [...county, ...rest]) {
    const o = p as Record<string, unknown>;
    const num = String(o["permit_number"] ?? o["number"] ?? o["permitNumber"] ?? o["record_id"] ?? o["id"] ?? "").trim().toLowerCase();
    if (num) { if (seen.has(num)) continue; seen.add(num); }
    out.push(p);
  }
  return out;
}

export function narrowLead(row: RawRow): Lead | null {
  const id = str(row.id);
  const address = str(row.address);
  if (!id || !address) return null;
  const raw = (row.raw ?? {}) as RawBlob;
  // Zillow sets the address COLUMN to the STREET only; the full "street, city,
  // ST zip" is in raw.full_address. displayAddress is for the TITLE ONLY —
  // lead.address stays the identity key (nat_key) so edits/tasks/deals lookups
  // don't break (Tom 2026-08-12: "Why the address isnt full"). Only used when
  // it's genuinely fuller and starts with the same street.
  const fullAddr = str(raw.full_address);
  const displayAddress = (fullAddr && fullAddr.length > address.length
    && fullAddr.toLowerCase().startsWith(address.toLowerCase()))
    ? fullAddr : address;
  const price = num(row.price);
  const arv = num(row.arv);
  let arvPct = num(raw.arv_percentage);
  if (arvPct === null && price !== null && arv !== null && arv > 0) arvPct = (price / arv) * 100;
  if (arvPct !== null) arvPct = Math.round(arvPct);
  const cdomRaw = num(raw.cdom) ?? num(raw.days_on_zillow) ?? num(raw.dom);
  const sellerName = str(raw.agent_name) ?? str(raw.seller) ?? str(raw.dispositions_manager);
  const sellerCo = str(raw.company_name) ?? str(raw.account_title);
  const seller = sellerName
    ? (sellerCo ? `${sellerName} · ${sellerCo}` : sellerName).slice(0, 60)
    : sellerCo
      ? sellerCo.slice(0, 60)
      : null;
  return {
    id,
    displayAddress,
    createdAt: row.created_at ? Date.parse(String(row.created_at)) || 0 : 0,
    address,
    source: str(row.source) ?? "InvestorLift",
    price,
    arv,
    arvPct,
    county: str(row.county),
    beds: num(row.beds),
    baths: num(row.baths),
    sqft: num(row.sqft),
    zest: num(raw.zestimate),
    rentZest: num(raw.rent_zestimate),
    cdom: cdomRaw !== null ? Math.round(cdomRaw) : null,
    seller,
    marketStatus: marketStatusOf(raw),
    distressed: (raw as Record<string, unknown>)["distressed"] === true,
    jobTag: jobTagOf(raw),
    priceDrop: priceDropOf(raw),
    reductions: Array.isArray(raw.price_drops) ? raw.price_drops.length : 0,
    priceDrops: priceDropsOf(raw),
    priceHistory: priceHistoryOf(raw),
    photos: photosOf(raw),
    desc: str(raw.description),
    taxHistory: taxHistoryOf(raw),
    yearBuilt: num(raw.year_built) ?? num(raw.year),
    // Display fallbacks for the PROPERTY INFORMATION grid (Tom 2026-08-16:
    // "why it doesnt show info after the enrich?"). Enrich writes its facts
    // into raw (county_owner, parcel_number, …) and seeds rec_lead_fields —
    // but when the org-wide fields read fails or lags (token-refresh races in
    // long-lived tabs), the page rendered "—" over data we already have. The
    // grid now falls back to these raw twins whenever the edit-field is empty.
    facts: factsOf(raw),
    possibleDupOf: str((raw as Record<string, unknown>)["possible_dup_of"]),
    permits: mergedPermitsOf(raw),
    // county permits on the record count as "checked" too (2026-09-14)
    permitsChecked: raw.permits_checked === true || raw.permitsChecked === true || countyPermitsOf(raw).length > 0,
    permitUrl: str(raw.permit_url) ?? str(raw.permitUrl),
    // Listing link for the source chip: InvestorLift marketplace page, or the
    // Zillow homedetails URL for Zillow rows (Tom 2026-08-12: "no zillow link")
    ilUrl: str(raw.property_page_url) ?? str(raw.property_url),
    zillowUrl: /zillow\.com/i.test(str(raw.property_url) ?? "") ? str(raw.property_url) : null,
    countyLink: str(raw.county_appraiser) ?? str(raw.county_link) ?? str(raw.countyLink),
    countySite: str(raw.county_site) ?? str(raw.countySite),
    mediaLinks: linkListOf(raw.media_links ?? raw.mediaLinks),
    photoLinks: linkListOf(raw.photo_links ?? raw.photoLinks ?? raw.contact_docs ?? raw.google_drive_links),
  };
}

export const fmtMoney = (n: number | null): string =>
  n === null ? "—" : moneyExact(n);
